import { ChangeDetectionStrategy, Component, computed, input, linkedSignal } from "@angular/core";
import { S25EvDetailsModule } from "../../../modules/object-details/event/s25.ev.details.module";
import { S25SimpleCollapseModule } from "../../../modules/s25-simple-collapse/s25.simple.collapse.module";
import { EventSummary, EventSummaryService } from "../../../modules/s25-swarm-schedule/s25.event.summary.service";
import { S25LoadingInlineModule } from "../../../modules/s25-loading/s25.loading.inline.module";
import {
    EventCopyOccurrencePayload,
    OccurrenceModel,
    ReservationI,
    SegmentOccurrenceCopyConfig,
} from "../../../pojo/ReservationI";
import { EvdetailService } from "../../../services/evdetail.service";
import { UserprefService } from "../../../services/userpref.service";
import { S25Util } from "../../../util/s25-util";
import { s25Resource } from "../../../util/s25.resource";
import { EventSummaryData } from "../../s25-event-summary/s25.event.summary.component";

type CopySegmentConfig = {
    profileId: number;
    profileName: string;
    profileInitStartDt: string;
    profileCode: string;
    reservations: ReservationI[];
};

@Component({
    selector: "s25-ng-event-copy-occurrence-mapping",
    standalone: true,
    imports: [S25EvDetailsModule, S25LoadingInlineModule, S25SimpleCollapseModule],
    template: `
        @if (isLoading()) {
            <s25-ng-loading-inline-static />
        } @else if (hasCopySegments()) {
            @if (hasMultipleSegments()) {
                <section class="copy-event-section">
                    <p class="copy-event-section__title">Select segments to copy</p>
                    <div class="copy-segment-checkbox-grid">
                        @for (segment of copySegments(); track segment.profileId) {
                            @if (segment.reservations?.length) {
                                <label class="copy-segment-checkbox">
                                    <input
                                        type="checkbox"
                                        [checked]="isSegmentSelected(segment.profileId)"
                                        (change)="toggleSegmentSelected(segment.profileId, $event)"
                                    />
                                    {{ segment.profileName }}
                                </label>
                            }
                        }
                    </div>
                </section>
            }
            @if (selectedCopySegments().length) {
                <section class="copy-event-section">
                    <div class="copy-event-section__header">
                        <p class="copy-event-section__title">Occurrence mapping</p>
                        @if (hasMultipleMappingPanels()) {
                            <button
                                type="button"
                                class="aw-button aw-button--outline"
                                (click)="toggleExpandAllSegments()"
                            >
                                {{ allSegmentsExpanded() ? "Collapse All" : "Expand All" }}
                            </button>
                        }
                    </div>
                    @for (segment of selectedCopySegments(); track segment.profileId) {
                        @if (hasMultipleMappingPanels()) {
                            <s25-simple-collapse
                                class="copy-event-segment-panel"
                                [header]="segment.profileName"
                                [titleText]="'Expand or collapse ' + segment.profileName"
                                [expanded]="isSegmentExpanded(segment.profileId)"
                                (expandedChange)="onSegmentExpandedChange(segment.profileId, $event)"
                            >
                                <div class="copy-event-segment-panel__body">
                                    <s25-ng-occurrences-config
                                        [eventId]="eventId()!"
                                        [dateFormat]="dateFormat()!"
                                        [timeFormat]="timeFormat()!"
                                        [initialOccurrences]="segment.reservations"
                                        [profileInitStartDt]="segment.profileInitStartDt"
                                        [profileCode]="segment.profileCode"
                                        [onChange]="updateSegmentOccurrences(segment.profileId)"
                                    />
                                </div>
                            </s25-simple-collapse>
                        } @else {
                            <div class="copy-event-segment-panel">
                                <s25-ng-occurrences-config
                                    [eventId]="eventId()!"
                                    [dateFormat]="dateFormat()!"
                                    [timeFormat]="timeFormat()!"
                                    [initialOccurrences]="segment.reservations"
                                    [profileInitStartDt]="segment.profileInitStartDt"
                                    [profileCode]="segment.profileCode"
                                    [onChange]="updateSegmentOccurrences(segment.profileId)"
                                />
                            </div>
                        }
                    }
                </section>
            }
        }
    `,
    styleUrls: ["./event.copy.occurrence.mapping.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventCopyOccurrenceMappingComponent {
    readonly eventId = input<number>();
    /** When provided (e.g. event details More Actions), skips summary/format fetches. */
    readonly eventData = input<EventSummaryData>();

    readonly profileOccurrences = s25Resource({
        if: () => Number.isFinite(this.eventId()),
        params: () => this.eventId()!,
        loader: async ({ params }) => (await EvdetailService.get(params))?.occur?.prof,
    });
    readonly summaryResource = s25Resource({
        if: () => !this.eventData()?.summary?.prof && Number.isFinite(this.eventId()),
        params: () => this.eventId()!,
        loader: async ({ params }) => (await EventSummaryService.getSummary(params)).summary,
    });
    readonly dateFormatResource = s25Resource({
        if: () => !this.eventData()?.dateFormat,
        loader: () => UserprefService.getS25Dateformat(),
    });
    readonly timeFormatResource = s25Resource({
        if: () => !this.eventData()?.timeFormat,
        loader: () => UserprefService.getS25Timeformat(),
    });

    readonly dateFormat = computed(() => this.eventData()?.dateFormat ?? this.dateFormatResource.value() ?? undefined);
    readonly timeFormat = computed(() => this.eventData()?.timeFormat ?? this.timeFormatResource.value() ?? undefined);
    readonly summaryProfiles = computed((): EventSummary.Profile[] => {
        return this.eventData()?.summary?.prof ?? this.summaryResource.value()?.prof ?? [];
    });

    readonly isLoading = computed(
        () =>
            this.profileOccurrences.resource.isLoading() ||
            this.summaryResource.resource.isLoading() ||
            this.dateFormatResource.resource.isLoading() ||
            this.timeFormatResource.resource.isLoading() ||
            (!!this.hasCopySegments() && (!this.dateFormat() || !this.timeFormat())),
    );

    readonly copySegments = computed((): CopySegmentConfig[] => {
        const summaryProfiles = this.summaryProfiles();
        const occurrenceProfiles = this.profileOccurrences.value() ?? [];

        const segmentSources = occurrenceProfiles.length
            ? occurrenceProfiles
            : summaryProfiles.map((summaryProfile) => ({
                  profId: summaryProfile.profile_id,
                  rsv: [] as CopySegmentConfig["reservations"],
              }));

        return segmentSources
            .map((occurrenceProfile, index) => {
                const profileId = Number(occurrenceProfile.profId ?? summaryProfiles[index]?.profile_id ?? index);
                const summaryProfile =
                    summaryProfiles.find((profile) => Number(profile.profile_id) === profileId) ??
                    summaryProfiles[index];
                const reservations = occurrenceProfile.rsv ?? [];
                const profileInitStartDt =
                    summaryProfile?.init_start_dt ??
                    (reservations[0]?.startDt ? String(reservations[0].startDt) : undefined);

                if (!profileInitStartDt) {
                    return undefined;
                }

                return {
                    profileId,
                    profileName: summaryProfile?.names ?? `Segment ${index + 1}`,
                    profileInitStartDt,
                    profileCode: summaryProfile?.profile_code ?? "adhoc",
                    reservations,
                };
            })
            .filter(S25Util.isDefined);
    });

    readonly hasCopySegments = computed(() => this.copySegments().some((segment) => segment.reservations?.length));
    readonly hasMultipleSegments = computed(() => this.copySegments().length > 1);
    readonly selectedSegmentIds = linkedSignal<Set<number>>(() => {
        const selectableProfileIds = this.copySegments()
            .filter((segment) => segment.reservations?.length)
            .map((segment) => segment.profileId);
        return new Set(selectableProfileIds);
    });
    readonly selectedCopySegments = computed(() =>
        this.copySegments().filter(
            (segment) => segment.reservations?.length && this.selectedSegmentIds().has(segment.profileId),
        ),
    );
    readonly hasMultipleMappingPanels = computed(() => this.selectedCopySegments().length > 1);
    /** Multiple panels may be open at once (Expand All); defaults to the first selected segment. */
    readonly expandedSegmentIds = linkedSignal<Set<number>>(() => {
        const firstProfileId = this.selectedCopySegments()[0]?.profileId;
        return firstProfileId != null ? new Set([firstProfileId]) : new Set();
    });
    readonly allSegmentsExpanded = computed(() => {
        const selectedSegments = this.selectedCopySegments();
        if (selectedSegments.length <= 1) {
            return false;
        }
        const expandedIds = this.expandedSegmentIds();
        return selectedSegments.every((segment) => expandedIds.has(segment.profileId));
    });
    readonly canCopy = computed(() => this.selectedCopySegments().length > 0 || !this.hasCopySegments());
    /** Seeded from source segments; child `onChange` updates mapped dates in place via `.update`. */
    readonly segmentOccurrenceConfigs = linkedSignal<SegmentOccurrenceCopyConfig[]>(() => {
        const eventId = this.eventId();
        return this.copySegments().map((segment) => ({
            sourceProfileId: segment.profileId,
            profileCode: segment.profileCode,
            occurrences:
                segment.reservations?.map((reservation) => ({
                    eventId,
                    startDt: reservation.startDt,
                    endDt: reservation.endDt,
                    locations: reservation.space ? [...reservation.space] : [],
                    resources: reservation.res ? [...reservation.res] : [],
                    rsrvId: reservation.rsvId,
                })) ?? [],
        }));
    });
    /** Whole-day delta from the source start date to the chosen copy start date. */
    readonly dayOffset = computed(() => {
        const originalSegment = this.selectedCopySegments()[0] ?? this.copySegments()[0];
        if (!originalSegment?.profileInitStartDt) {
            return 0;
        }
        const mappedSegment = this.segmentOccurrenceConfigs().find(
            (segment) => segment.sourceProfileId === originalSegment.profileId,
        );
        const mappedStart = mappedSegment?.occurrences?.[0]?.startDt;
        if (!mappedStart) {
            return 0;
        }
        return S25Util.date.diffDays(originalSegment.profileInitStartDt, mappedStart);
    });

    getRoseOccurrencePayload(itemIds?: number[]): EventCopyOccurrencePayload | undefined {
        const eventId = this.eventId();
        if (typeof eventId !== "number" || !Number.isFinite(eventId)) {
            return undefined;
        }

        const dayOffset = this.dayOffset();
        const includePrimarySegments = !itemIds?.length || itemIds.some((id) => Number(id) === eventId);

        let segments: SegmentOccurrenceCopyConfig[] = [];
        if (includePrimarySegments) {
            const selectedProfileIds = this.selectedSegmentIds();
            segments = this.segmentOccurrenceConfigs().filter(
                (segment) => selectedProfileIds.has(segment.sourceProfileId) && segment.occurrences?.length,
            );
        }

        if (!segments.length && !dayOffset) {
            return undefined;
        }

        return { segments, dayOffset };
    }

    isSegmentSelected(profileId: number): boolean {
        return this.selectedSegmentIds().has(profileId);
    }

    isSegmentExpanded(profileId: number): boolean {
        return this.expandedSegmentIds().has(profileId);
    }

    onSegmentExpandedChange(profileId: number, expanded: boolean): void {
        this.expandedSegmentIds.update((expandedIds) => {
            const nextExpandedIds = new Set(expandedIds);
            if (expanded) {
                nextExpandedIds.add(profileId);
            } else {
                nextExpandedIds.delete(profileId);
            }
            return nextExpandedIds;
        });
    }

    expandAllSegments(): void {
        this.expandedSegmentIds.set(new Set(this.selectedCopySegments().map((segment) => segment.profileId)));
    }

    collapseAllSegments(): void {
        this.expandedSegmentIds.set(new Set());
    }

    toggleExpandAllSegments(): void {
        if (this.allSegmentsExpanded()) {
            this.collapseAllSegments();
        } else {
            this.expandAllSegments();
        }
    }

    toggleSegmentSelected(profileId: number, event: Event): void {
        const checked = (event.target as HTMLInputElement).checked;
        this.selectedSegmentIds.update((selectedIds) => {
            const nextSelectedIds = new Set(selectedIds);
            if (checked) {
                nextSelectedIds.add(profileId);
            } else {
                nextSelectedIds.delete(profileId);
            }
            return nextSelectedIds;
        });
    }

    updateSegmentOccurrences(profileId: number): (occurrences: OccurrenceModel[]) => void {
        return (occurrences: OccurrenceModel[]) => {
            this.segmentOccurrenceConfigs.update((segments) =>
                segments.map((segment) =>
                    segment.sourceProfileId === profileId
                        ? {
                              ...segment,
                              profileCode: occurrences[0]?.profile_code ?? segment.profileCode,
                              mapping: occurrences[0]?.mapping ?? segment.mapping,
                              occurrences: occurrences.map((occurrence) => ({
                                  ...occurrence,
                                  eventId: this.eventId(),
                                  profile_code: occurrences[0]?.profile_code ?? segment.profileCode,
                              })),
                          }
                        : segment,
                ),
            );
        };
    }
}
